import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Camera, Zap, ArrowRight } from "lucide-react";
import api, { resolveImageUrl } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

const resolveImg = (url, fallback) => resolveImageUrl(url, fallback);

export default function Landing() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [bib, setBib] = useState("");
  const [eventId, setEventId] = useState("");

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (bib) params.set("bib", bib);
    if (eventId) params.set("event_id", eventId);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="App">
      <Seo
        title="Foton Enduroskola — Hitta dina enduro-bilder via startnummer"
        description="Officiell fotoplattform för enduro. Sök på startnummer och hitta dina bilder från Novemberkåsan, Gotland, SM och fler event. AI-taggning, snabba nedladdningar och säker checkout."
        path="/"
      />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden hero-noise">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/28610282/pexels-photo-28610282.jpeg"
            alt="Enduro hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl">
            <p className="label-overline mb-4" data-testid="hero-overline">
              ★ Officiella fotograferingar · 4 event aktiva
            </p>
            <h1
              className="font-display font-black uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.9] mb-6"
              data-testid="hero-heading"
            >
              Hitta dina <span className="text-brand">endurofoton</span> på sekunder.
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-xl mb-8">
              Sök på startnummer. Förhandsgranska. Köp och ladda ner originalbilder direkt — utan vattenstämpel.
            </p>

            <form
              onSubmit={handleSearch}
              className="bg-black/60 backdrop-blur-md border border-white/10 p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 max-w-2xl"
              data-testid="hero-search-form"
            >
              <select
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                className="input-field font-display uppercase"
                data-testid="hero-event-select"
              >
                <option value="">Alla event</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name}
                  </option>
                ))}
              </select>
              <input
                value={bib}
                onChange={(e) => setBib(e.target.value)}
                placeholder="STARTNUMMER (T.EX. 143)"
                className="input-field font-display uppercase tracking-wider text-lg"
                data-testid="hero-bib-input"
              />
              <button type="submit" className="btn-primary flex items-center justify-center gap-2" data-testid="hero-search-btn">
                <Search className="w-4 h-4" /> Sök
              </button>
            </form>

            <div className="flex flex-wrap items-center gap-6 mt-8 text-white/60 text-sm">
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-brand" /> AI-detektering av startnummer</span>
              <span className="flex items-center gap-2"><Camera className="w-4 h-4 text-brand" /> Stöttar fotografer & förare</span>
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label-overline">Album & event</p>
            <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl mt-2">
              Bläddra i fotoalbumen
            </h2>
          </div>
          <Link to="/search" className="btn-ghost hidden sm:flex items-center gap-1" data-testid="view-all-events">
            Visa alla <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {events.map((ev, i) => (
            <Link
              key={ev.id}
              to={ev.slug ? `/event/${ev.slug}` : `/search?event_id=${ev.id}`}
              className="card-surface group block overflow-hidden gallery-item animate-fade-in-up"
              style={{ animationDelay: `${i * 80}ms`, opacity: 0 }}
              data-testid={`event-card-${ev.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#1a1a1a]">
                <img
                  src={resolveImg(ev.cover_image_url, "https://images.pexels.com/photos/28610282/pexels-photo-28610282.jpeg")}
                  alt={ev.name}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-xs text-brand font-display uppercase tracking-wider">{ev.series || "Event"}</p>
                  <h3 className="font-display font-bold text-white uppercase text-lg leading-tight">{ev.name}</h3>
                </div>
              </div>
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/50 uppercase tracking-wider">{ev.date}</p>
                  <p className="text-sm text-white/80">{ev.location}</p>
                </div>
                <p className="font-display text-brand font-bold">{ev.photo_count || 0} foton</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0d0d0d] border-y border-white/5 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="label-overline mb-3">Så funkar det</p>
          <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl mb-12 max-w-2xl">
            Tre steg — hitta, förhandsgranska, ladda ner.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Sök på startnummer", d: "Välj event och skriv in ditt startnummer. AI-systemet hittar alla bilder där du är med." },
              { n: "02", t: "Granska och välj", d: "Snabba förhandsgranskningar med vattenstämpel. Lägg dina favoriter i varukorgen." },
              { n: "03", t: "Köp och ladda ner", d: "Säker betalning via Stripe. Originalfilen utan stämpel laddas ner direkt." },
            ].map((s) => (
              <div key={s.n} className="border-l-2 border-brand pl-6 py-2" data-testid={`step-${s.n}`}>
                <p className="font-display text-6xl font-black text-brand/30 leading-none">{s.n}</p>
                <h3 className="font-display font-bold uppercase text-white text-2xl mt-3">{s.t}</h3>
                <p className="text-white/70 mt-2 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR PHOTOGRAPHERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src="/assets/photographer-hero.jpg"
            alt="Enduroförare wheelie"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
            <p className="font-display text-brand font-bold uppercase tracking-wider text-sm">För fotografer</p>
          </div>
        </div>
        <div>
          <p className="label-overline mb-3">Fotograf?</p>
          <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl mb-6">
            Ladda upp. Sälj.<br />Få betalt.
          </h2>
          <ul className="space-y-3 text-white/80 mb-8">
            {[
              "Bulk-uppladdning med drag & drop",
              "AI taggar startnummer automatiskt",
              "Du sätter priset per foto – eller kör vår bundle-mall",
              "Sponsra förare direkt genom din sälj",
              "Globalt CDN för snabb leverans",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="w-1 h-6 bg-brand mt-1" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Link to="/photographer/register" className="btn-primary inline-flex items-center gap-2" data-testid="cta-become-photographer">
            Publicera dina foton <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
